import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Tooltip,
  Link,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MutateOptions } from 'react-query';

import { requisitesApi } from 'api';
import { RequisitesInfo, RequisitesStatusInfo } from 'components';
import { RequisitesStatus } from 'enums';
import { useMutation, useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Requisites } from 'types';

type Props = {
  requisites: Requisites;
  onClick: () => void;
  onStatusUpdate?: (requisites: Requisites) => void;
  readOnly?: boolean;
  showTrader?: boolean;
};

export const ExpandableRequisites: React.FC<Props> = ({
  requisites,
  onClick,
  onStatusUpdate,
  readOnly,
  showTrader,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.requisites',
  });
  const { isTrader, isAdminOrTechOperator } = useUser();

  const { mutate: updateRequisiteStatus } = useMutation(
    requisitesApi.updateStatus,
    {
      notifierType: 'execute',
      onSuccess: onStatusUpdate,
    },
  );

  const handleStatusChange = useCallback(
    (
      id: string,
      status: RequisitesStatus,
      options?: MutateOptions<any, any, any, any>,
    ) => {
      updateRequisiteStatus(
        {
          id,
          requisites: { status },
        },
        options,
      );
    },
    [updateRequisiteStatus],
  );

  const requisitesLabel = useMemo(() => {
    const name = requisites.name || `#${requisites.id}`;
    return (
      name +
      (showTrader
        ? requisites.user
          ? ` (${requisites.user.name})`
          : ` (#${requisites.traderId})`
        : '')
    );
  }, [requisites, showTrader]);

  return (
    <>
      <Accordion disableGutters={true}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            padding: '0px 2px !important',
            minHeight: '20px',
          }}
        >
          <Fragment>
            {requisites.status !== RequisitesStatus.Blocked ? (
              <Tooltip title={t('requisites_details.edit_requisites.title')}>
                <Link
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                  }}
                  color={
                    requisites.status === RequisitesStatus.Active
                      ? 'primary'
                      : 'textSecondary'
                  }
                >
                  <span className="tw-text-sm">{requisitesLabel}</span>
                </Link>
              </Tooltip>
            ) : (
              <Typography
                variant="subtitle1"
                color="textSecondary"
                className="tw-text-sm"
              >
                {requisitesLabel}
              </Typography>
            )}
          </Fragment>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            padding: '4px 2px !important',
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
            <RequisitesInfo
              showName={false}
              requisite={requisites}
              compact
              className="tw-text-xs"
            />
            <RequisitesStatusInfo
              requisites={requisites}
              canChangeStatus={!readOnly && (isAdminOrTechOperator || isTrader)}
              updateStatus={handleStatusChange}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
